<template>
    <div class="bilibili-login-container">
        <!-- 返回首页按钮 -->
        <el-button type="primary" icon="el-icon-arrow-left" class="back-button" @click="goBack">
            < 返回首页
        </el-button>

        <!-- 登录主内容 -->
        <div class="login-content">
            <h2 class="login-title">B站扫码登录</h2>

            <!-- 二维码区域 -->
            <div class="qrcode-box">
                <div v-if="loading" class="loading-state">
                    <el-icon class="loading-icon">
                        <Loading />
                    </el-icon>
                    <span>正在获取二维码...</span>
                </div>

                <div v-else-if="error" class="error-state">
                    <el-icon class="error-icon">
                        <Warning />
                    </el-icon>
                    <span>{{ error }}</span>
                    <el-button type="primary" @click="getQRCode">重试</el-button>
                </div>

                <div v-else class="qrcode-content">
                    <img :src="qrCodeImage" class="qrcode-image" alt="B站登录二维码" @click="newimg">
                    <p class="qrcode-tip">请使用B站APP扫码登录</p>

                    <div class="status-message" :class="statusClass">
                        <el-icon v-if="status === 'success'">
                            <CircleCheck />
                        </el-icon>
                        <el-icon v-else-if="status === 'error'">
                            <CircleClose />
                        </el-icon>
                        <span>{{ statusMessage }}</span>
                    </div>
                </div>
            </div>

            <!-- 登录说明 -->
            <div class="login-instructions">
                <h3>登录说明：</h3>
                <ol>
                    <li>打开手机上的B站APP</li>
                    <li>点击右上角"+"号，选择"扫一扫"</li>
                    <li>扫描上方二维码完成登录</li>
                </ol>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
    Loading,
    Warning,
    CircleCheck,
    CircleClose
} from '@element-plus/icons-vue'
import axios from 'axios'
import QRCode from 'qrcode'

const router = useRouter()

// 状态管理
const loading = ref(true)
const error = ref(null)
const qrCodeImage = ref('')
const qrCodeKey = ref('')
const status = ref('') // 'waiting', 'scanning', 'success', 'error'
const statusMessage = ref('等待扫码...')
const statusClass = ref('')
let loginPollInterval = null // 定义轮询定时器变量

// 获取二维码
const getQRCode = async () => {
    try {
        // 先停止可能存在的旧轮询
        stopLoginPolling()
        
        loading.value = true
        error.value = null
        status.value = 'waiting'
        statusMessage.value = '等待扫码...'
        statusClass.value = ''

        const response = await axios.get('/adminapi/user/login')

        if (response.data.status === 'success') {
            const loginUrl = response.data.data.data.url
            qrCodeKey.value = response.data.data.data.qrcode_key

            // 生成二维码图片
            QRCode.toDataURL(loginUrl, { width: 200 }, (err, url) => {
                if (err) throw err
                qrCodeImage.value = url
                loading.value = false

                // 开始轮询登录状态
                loginPollInterval = pollLoginStatus()
            })
        } else {
            throw new Error(response.data.message || '获取二维码失败')
        }
    } catch (err) {
        console.error('获取二维码失败:', err)
        error.value = err.message || '获取二维码失败，请重试'
        loading.value = false
        status.value = 'error'
        statusMessage.value = error.value
        statusClass.value = 'error'
    }
}

// 轮询登录状态
const pollLoginStatus = () => {
    return setInterval(async () => {
        try {
            const response = await axios.post('/adminapi/user/MonitorLogin', {
                oauthKey: qrCodeKey.value
            }, {
                withCredentials: true
            });

            // 检查响应数据结构
            // console.log('登录状态检查响应:', response.data.data);

            if (response.data.data.code === 0) {
                // 扫码登录成功 — 后端已将SESSDATA存入session
                stopLoginPolling();
                status.value = 'success';
                statusMessage.value = '登录成功!';
                statusClass.value = 'success';

                // 成功后获取用户信息并缓存到本地
                const fetchUserAndCache = async () => {
                    try {
                        const userRes = await axios.get('/adminapi/user/Bmsg', { withCredentials: true });
                        if (userRes.data.code === 0 && userRes.data.data) {
                            const userData = userRes.data.data;
                            localStorage.setItem('userInfo', JSON.stringify({
                                uname: userData.uname || '',
                                mid: userData.mid || '',
                                face: userData.face || ''
                            }));
                            console.log('登录成功，用户数据已缓存:', userData.uname);
                            return true;
                        }
                        return false;
                    } catch (e) {
                        console.error('获取用户信息失败:', e.message);
                        return false;
                    }
                };

                let success = await fetchUserAndCache();
                if (!success) {
                    // 等待1.5秒后重试一次（后端可能还在处理session）
                    await new Promise(r => setTimeout(r, 1500));
                    success = await fetchUserAndCache();
                }
                if (!success) {
                    console.warn('登录后未能获取用户信息，请刷新页面重试');
                }

                // 1.5秒后跳转首页
                setTimeout(() => {
                    router.push('/');
                }, 1500);
            } else if (response.data.data.code === 86038) {
                // 二维码已过期
                stopLoginPolling();
                status.value = 'error';
                statusMessage.value = '二维码已过期，请刷新重试';
                statusClass.value = 'error';
            } else if (response.data.data.code === 86090) {
                // 已扫码未确认
                status.value = 'scanning';
                statusMessage.value = '已扫码，请在APP上确认登录';
                statusClass.value = 'scanning';
            } else if (response.data.data.code === 86101) {
                // 等待扫码
                status.value = 'waiting';
                statusMessage.value = '等待扫码...';
                statusClass.value = '';
            }
        } catch (err) {
            console.error('检查登录状态失败:', err);
            stopLoginPolling();
            status.value = 'error';
            statusMessage.value = '登录状态检查失败';
            statusClass.value = 'error';
        }
    }, 3000); // 每3秒检查一次
}

// 停止轮询
const stopLoginPolling = () => {
    if (loginPollInterval) {
        clearInterval(loginPollInterval);
        loginPollInterval = null;
        console.log('轮询已停止');
    }
}

// 返回首页
const goBack = () => {
    stopLoginPolling(); // 点击返回时停止轮询
    router.push('/')
}

// 组件挂载时获取二维码
onMounted(() => {
    getQRCode()
})

// 组件卸载时停止轮询
onUnmounted(() => {
    stopLoginPolling();
})

const newimg = () => {
    getQRCode()
}
</script>

<style scoped>
.bilibili-login-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    position: relative;
}

.back-button {
    position: absolute;
    left: 20px;
    top: 20px;
}

.login-content {
    padding: 40px;
    text-align: center;
}

.login-title {
    color: #fb7299;
    margin-bottom: 30px;
}

.qrcode-box {
    margin: 0 auto;
    width: 240px;
    padding: 20px;
    border: 1px dashed #e5e5e5;
    border-radius: 8px;
    background-color: #f9f9f9;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
}

.loading-icon {
    font-size: 40px;
    margin-bottom: 15px;
    color: #fb7299;
    animation: rotate 2s linear infinite;
}

.error-icon {
    font-size: 40px;
    margin-bottom: 15px;
    color: #f56c6c;
}

.qrcode-image {
    width: 200px;
    height: 200px;
    display: block;
    margin: 0 auto;
}

.qrcode-tip {
    margin-top: 15px;
    color: #666;
    font-size: 14px;
}

.status-message {
    margin-top: 15px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-message i {
    margin-right: 5px;
}

.waiting {
    color: #666;
}

.scanning {
    color: #e6a23c;
}

.success {
    color: #67c23a;
}

.error {
    color: #f56c6c;
}

.login-instructions {
    margin-top: 30px;
    text-align: left;
    padding: 0 40px;
}

.login-instructions h3 {
    color: #333;
    margin-bottom: 10px;
}

.login-instructions ol {
    padding-left: 20px;
    color: #666;
    line-height: 1.8;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>